import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiEdit, FiTrash2, FiPlus, FiFileText, 
  FiBell, FiInfo, FiEye, FiSearch, FiChevronRight,
  FiHome, FiTv, FiSettings, FiBriefcase, FiTruck,
  FiMap, FiList, FiAlertCircle, FiGrid, FiLayers, FiMove
} from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';

import { api } from '../../api';
import toast from 'react-hot-toast';

// Extend Quill's Link format to allow mailto: and tel: protocols
const QuillLink = Quill.import('formats/link');
class CustomLink extends QuillLink {
  static sanitize(url) {
    const sanitizedUrl = super.sanitize(url);
    if (!sanitizedUrl) {
      // Allow mailto: and tel: protocols
      if (url.startsWith('mailto:') || url.startsWith('tel:')) {
        return url;
      }
    }
    return sanitizedUrl;
  }
}
Quill.register(CustomLink, true);

// Category icon mapping
const categoryIcons = {
  'home': FiHome,
  'tv': FiTv,
  'settings': FiSettings,
  'bed': FiBriefcase,
  'car': FiTruck,
  'map': FiMap,
  'list': FiList,
  'alert': FiAlertCircle
};

// Category color mapping for subtle backgrounds
const categoryColors = {
  'welcome': 'bg-blue-50 border-blue-200',
  'av-technology': 'bg-purple-50 border-purple-200',
  'house-operations': 'bg-green-50 border-green-200',
  'rooms-spaces': 'bg-orange-50 border-orange-200',
  'transportation': 'bg-indigo-50 border-indigo-200',
  'local-info': 'bg-pink-50 border-pink-200',
  'house-rules': 'bg-yellow-50 border-yellow-200',
  'emergency': 'bg-red-50 border-red-200'
};

const SectionModal = ({ section, categories, onClose, onSave }) => {
  // Check if this is a real section object or just a default category selection
  const isNewSection = !section || !section.id;
  const defaultCategoryId = section?.category_id || (categories && categories.length > 0 ? categories[0].id : '');
  
  const [formData, setFormData] = useState({
    category_id: defaultCategoryId,
    title: section?.title || '',
    slug: section?.slug || '',
    content: section?.content || '',
    order_index: section?.order_index || 999,
    notify_on_access: section?.notify_on_access || false,
    notify_emails: section?.notify_emails || '',
    search_keywords: section?.search_keywords || ''
  });

  const [editingSlug, setEditingSlug] = useState(isNewSection);
  const [showSourceCode, setShowSourceCode] = useState(false);
  const [sourceContent, setSourceContent] = useState(section?.content || '');
  const quillRef = useRef(null);

  // Custom image handler for ReactQuill
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        // Show loading toast
        const loadingToast = toast.loading('Uploading image...');
        
        // Upload the image
        const response = await api.uploadMedia(file);
        const imageUrl = response.data.url;
        
        // Get the quill editor instance
        const quill = quillRef.current?.getEditor();
        if (quill) {
          // Get current cursor position
          const range = quill.getSelection(true);
          // Insert the image at cursor position
          quill.insertEmbed(range.index, 'image', imageUrl);
          // Move cursor after the image
          quill.setSelection(range.index + 1);
        }
        
        toast.dismiss(loadingToast);
        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Image upload failed:', error);
        toast.error('Failed to upload image');
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    // If in source code mode, save the source content
    const finalData = showSourceCode 
      ? { ...formData, content: sourceContent }
      : formData;
    onSave(finalData);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-display text-lodge-brown-800">
            {isNewSection ? 'Create Section' : 'Edit Section'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                required
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setFormData({ 
                    ...formData, 
                    title,
                    slug: section ? formData.slug : generateSlug(title)
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short URL
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500 ${
                      !editingSlug ? 'bg-gray-50' : ''
                    }`}
                    pattern="[a-z0-9\-]+"
                    required
                    placeholder="e.g., hot-tub-instructions"
                    readOnly={!editingSlug}
                  />
                  <button
                    type="button"
                    onClick={() => setEditingSlug(!editingSlug)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {editingSlug ? 'Lock' : 'Edit'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  URL: /section/{formData.slug || 'your-url-here'}
                </p>
                <p className="text-xs text-gray-500">
                  Use lowercase letters, numbers, and hyphens - no spaces or special characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order
              </label>
              <input
                type="number"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              />
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <button
                type="button"
                onClick={() => {
                  if (showSourceCode) {
                    // Switching back to WYSIWYG - save source content
                    setFormData({ ...formData, content: sourceContent });
                  } else {
                    // Switching to source - load current content
                    setSourceContent(formData.content);
                  }
                  setShowSourceCode(!showSourceCode);
                }}
                className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {showSourceCode ? 'Visual Editor' : 'Source Code'}
              </button>
            </div>
            
            {showSourceCode ? (
              <div>
                <textarea
                  value={sourceContent}
                  onChange={(e) => setSourceContent(e.target.value)}
                  className="w-full h-80 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
                  placeholder="Paste HTML content here, including YouTube iframe embed codes..."
                />
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                  <strong>YouTube Embed Instructions:</strong>
                  <ol className="mt-1 ml-4 list-decimal">
                    <li>Go to any YouTube video</li>
                    <li>Click "Share" → "Embed"</li>
                    <li>Copy the entire iframe code</li>
                    <li>Paste it directly here</li>
                  </ol>
                  <div className="mt-2 font-mono text-xs bg-white p-2 rounded border border-blue-200">
                    Example: &lt;iframe width="560" height="315" src="https://www.youtube.com/embed/..."&gt;&lt;/iframe&gt;
                  </div>
                </div>
              </div>
            ) : (
              <>
                <ReactQuill
                  ref={quillRef}
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  className="h-64 mb-12"
                  modules={{
                    toolbar: {
                      container: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ],
                      handlers: {
                        image: imageHandler
                      }
                    }
                  }}
                />
                <div className="mt-2 text-xs text-gray-500">
                  Tip: Use the "Source Code" button above to embed YouTube videos or other HTML content
                </div>
              </>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Keywords
            </label>
            <input
              type="text"
              value={formData.search_keywords}
              onChange={(e) => setFormData({ ...formData, search_keywords: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              placeholder="e.g., dinner, lunch, breakfast, food"
            />
            <p className="mt-1 text-xs text-gray-500">
              Hidden keywords that help users find this section via search. Separate with commas.
            </p>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="notify"
                checked={formData.notify_on_access}
                onChange={(e) => setFormData({ ...formData, notify_on_access: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="notify" className="flex items-center text-sm font-medium text-gray-700">
                <FiBell className="mr-1" />
                Send notification when accessed
              </label>
            </div>
            
            {formData.notify_on_access && (
              <input
                type="email"
                placeholder="Notification email addresses (comma separated)"
                value={formData.notify_emails}
                onChange={(e) => setFormData({ ...formData, notify_emails: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              />
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {isNewSection ? 'Create Section' : 'Update Section'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Section Card Component
const SectionCard = ({ section, onEdit, onDelete, dragHandleProps, isDragging }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow ${
        isDragging ? 'shadow-2xl opacity-50' : ''
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-start space-x-2">
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="cursor-move text-gray-400 hover:text-gray-600 mt-1"
              title="Drag to reorder"
            >
              <FiMove className="w-4 h-4" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-lg font-medium text-mountain-gray-900">{section.title}</h3>
            <p className="text-sm text-gray-500 font-mono">/section/{section.slug}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/section/${section.slug}`}
            target="_blank"
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="View"
          >
            <FiEye className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onEdit(section)}
            className="p-2 text-lodge-brown-400 hover:text-lodge-brown-600 transition-colors"
            title="Edit"
          >
            <FiEdit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(section)}
            className="p-2 text-red-400 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {section.content && (
        <p className="text-sm text-gray-600 line-clamp-2 mb-3"
           dangerouslySetInnerHTML={{ 
             __html: section.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' 
           }}
        />
      )}
      
      <div className="flex items-center space-x-3">
        {section.is_active ? (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
            Inactive
          </span>
        )}
        {section.notify_on_access && (
          <span className="inline-flex items-center text-xs text-sky-blue-600">
            <FiBell className="w-3 h-3 mr-1" />
            Notifications
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Category Sidebar Component
const CategorySidebar = ({ categories, selectedCategory, onSelectCategory, sectionCounts }) => {
  return (
    <div className="w-64 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-mountain-gray-900">Categories</h2>
        <FiLayers className="w-5 h-5 text-gray-400" />
      </div>
      
      <nav className="space-y-1">
        <button
          onClick={() => onSelectCategory('all')}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
            selectedCategory === 'all'
              ? 'bg-lodge-brown-100 text-lodge-brown-800'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          <div className="flex items-center">
            <FiGrid className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">All Sections</span>
          </div>
          <span className="text-xs text-gray-500">
            {Object.values(sectionCounts).reduce((sum, count) => sum + count, 0)}
          </span>
        </button>
        
        <div className="border-t border-gray-200 my-2"></div>
        
        {categories.map((category) => {
          const Icon = categoryIcons[category.icon] || FiFileText;
          const count = sectionCounts[category.id] || 0;
          
          return (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                selectedCategory === category.id
                  ? 'bg-lodge-brown-100 text-lodge-brown-800'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <Icon className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{category.name}</span>
              </div>
              <span className="text-xs text-gray-500">{count}</span>
            </button>
          );
        })}
      </nav>
      
      <div className="mt-6 pt-6 border-t border-gray-200">
        <Link
          to="/admin/quick-info"
          className="flex items-center justify-between px-3 py-2 rounded-lg bg-sky-blue-50 hover:bg-sky-blue-100 transition-colors"
        >
          <div className="flex items-center">
            <FiInfo className="w-4 h-4 mr-2 text-sky-blue-600" />
            <span className="text-sm font-medium text-sky-blue-900">Quick Info</span>
          </div>
          <FiChevronRight className="w-4 h-4 text-sky-blue-600" />
        </Link>
        <p className="text-xs text-gray-500 mt-2 px-3">
          Manage homepage badges
        </p>
      </div>
    </div>
  );
};

const AdminSections = () => {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: categoriesResponse } = useQuery('categories', () => api.getCategories());
  const { data: sectionsResponse, isLoading } = useQuery(
    ['sections', 'all'], // Always fetch all sections
    () => api.getSections()
  );

  const categories = categoriesResponse?.data || [];
  const allSections = sectionsResponse?.data || [];
  
  // Sort categories by order_index to match the categories page
  const sortedCategories = [...categories].sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999));
  
  // Calculate section counts per category
  const sectionCounts = useMemo(() => {
    const counts = {};
    allSections.forEach(section => {
      counts[section.category_id] = (counts[section.category_id] || 0) + 1;
    });
    return counts;
  }, [allSections]);
  
  // Filter sections based on selected category and search
  const filteredSections = useMemo(() => {
    let filtered = allSections;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category_id === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(s => 
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.slug.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort by order_index
    return filtered.sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999));
  }, [allSections, selectedCategory, searchQuery]);
  
  // Group sections by category for "all" view
  const groupedSections = useMemo(() => {
    if (selectedCategory !== 'all') return null;
    
    const groups = {};
    filteredSections.forEach(section => {
      if (!groups[section.category_id]) {
        groups[section.category_id] = {
          category: sortedCategories.find(c => c.id === section.category_id),
          sections: []
        };
      }
      groups[section.category_id].sections.push(section);
    });
    
    // Sort sections within each group by order_index
    Object.values(groups).forEach(group => {
      group.sections.sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999));
    });
    
    return Object.values(groups).sort((a, b) => 
      (a.category?.order_index ?? 999) - (b.category?.order_index ?? 999)
    );
  }, [filteredSections, sortedCategories, selectedCategory]);

  const createMutation = useMutation(
    (data) => api.createSection(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sections');
        toast.success('Section created successfully');
        setShowModal(false);
      },
      onError: () => toast.error('Failed to create section')
    }
  );

  const updateMutation = useMutation(
    ({ id, ...data }) => api.updateSection(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sections');
        toast.success('Section updated successfully');
        setShowModal(false);
        setEditingSection(null);
      },
      onError: () => toast.error('Failed to update section')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.deleteSection(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sections');
        toast.success('Section deleted successfully');
      },
      onError: () => toast.error('Failed to delete section')
    }
  );

  const reorderMutation = useMutation(
    (sections) => api.reorderSections(sections),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('sections');
        toast.success('Sections reordered successfully');
      },
      onError: () => toast.error('Failed to reorder sections')
    }
  );

  const handleSave = (data) => {
    // Ensure category_id is an integer
    const formattedData = {
      ...data,
      category_id: parseInt(data.category_id),
      order_index: parseInt(data.order_index)
    };
    
    console.log('Saving section:', formattedData);
    console.log('editingSection:', editingSection);
    
    if (editingSection && editingSection.id) {
      updateMutation.mutate({ id: editingSection.id, ...formattedData });
    } else {
      createMutation.mutate(formattedData);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setShowModal(true);
  };

  const handleDelete = (section) => {
    if (window.confirm(`Are you sure you want to delete "${section.title}"?`)) {
      deleteMutation.mutate(section.id);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    // Get the category ID from the droppableId
    const droppableId = result.source.droppableId;
    let categorySections;
    
    if (selectedCategory === 'all') {
      // In 'all' view, get the category ID from droppableId
      const categoryId = parseInt(droppableId.replace('category-', ''));
      categorySections = allSections.filter(s => s.category_id === categoryId);
    } else {
      // In single category view, use filteredSections
      categorySections = filteredSections;
    }

    // Reorder the sections
    const items = Array.from(categorySections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order indices - start from the minimum existing order
    const minOrder = Math.min(...categorySections.map(s => s.order_index ?? 999));
    const sectionsWithNewOrder = items.map((section, index) => ({
      id: section.id,
      order_index: minOrder + index
    }));

    reorderMutation.mutate(sectionsWithNewOrder);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-display text-lodge-brown-800">Content Management</h1>
      </div>

      {/* Main Layout */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <CategorySidebar
          categories={sortedCategories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          sectionCounts={sectionCounts}
        />
        
        {/* Main Content Area */}
        <div className="flex-1">
          {/* Search and Add Section Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search sections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              />
            </div>
            
            <button
              onClick={() => {
                if (sortedCategories.length === 0) {
                  toast.error('Please create a category first');
                  return;
                }
                setEditingSection(null);
                setShowModal(true);
              }}
              className="btn-primary flex items-center ml-4"
            >
              <FiPlus className="mr-2" />
              Add Section
            </button>
          </div>
          
          {/* Content Display */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
            </div>
          ) : filteredSections.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchQuery ? 'No sections match your search' : 'No sections found'}
              </p>
              {selectedCategory !== 'all' && !searchQuery && (
                <button
                  onClick={() => {
                    const category = sortedCategories.find(c => c.id === selectedCategory);
                    if (category) {
                      setEditingSection(null);
                      setShowModal(true);
                    }
                  }}
                  className="mt-4 text-lodge-brown-600 hover:text-lodge-brown-700 font-medium"
                >
                  + Add first section to this category
                </button>
              )}
            </div>
          ) : selectedCategory === 'all' && groupedSections ? (
            /* Grouped View for All Sections */
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="space-y-8">
                {groupedSections.map(({ category, sections }) => {
                  if (!category) return null;
                  const Icon = categoryIcons[category.icon] || FiFileText;
                  const colorClass = categoryColors[category.slug] || 'bg-gray-50 border-gray-200';
                  
                  return (
                    <div key={category.id}>
                      <div className={`rounded-lg border ${colorClass} p-4 mb-4`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <Icon className="w-5 h-5 mr-2 text-gray-600" />
                            <h3 className="text-lg font-semibold text-mountain-gray-900">
                              {category.name}
                            </h3>
                            <span className="ml-2 text-sm text-gray-500">
                              ({sections.length} section{sections.length !== 1 ? 's' : ''})
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setEditingSection({ category_id: category.id });
                              setShowModal(true);
                            }}
                            className="text-sm text-lodge-brown-600 hover:text-lodge-brown-700 font-medium"
                          >
                            + Add Section
                          </button>
                        </div>
                        
                        <Droppable droppableId={`category-${category.id}`}>
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="grid gap-3"
                            >
                              <AnimatePresence>
                                {sections.map((section, index) => (
                                  <Draggable
                                    key={section.id}
                                    draggableId={`section-${section.id}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                      >
                                        <SectionCard
                                          section={section}
                                          onEdit={handleEdit}
                                          onDelete={handleDelete}
                                          dragHandleProps={provided.dragHandleProps}
                                          isDragging={snapshot.isDragging}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              </AnimatePresence>
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DragDropContext>
          ) : (
            /* Single Category View */
            <div>
              {selectedCategory !== 'all' && (
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    {(() => {
                      const category = sortedCategories.find(c => c.id === selectedCategory);
                      const Icon = category ? categoryIcons[category.icon] || FiFileText : FiFileText;
                      return (
                        <>
                          <Icon className="w-5 h-5 mr-2 text-gray-600" />
                          <h2 className="text-xl font-semibold text-mountain-gray-900">
                            {category?.name || 'Category'}
                          </h2>
                          <span className="ml-2 text-sm text-gray-500">
                            ({filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''})
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      setEditingSection(null);
                      setShowModal(true);
                    }}
                    className="text-sm text-lodge-brown-600 hover:text-lodge-brown-700 font-medium"
                  >
                    + Add Section
                  </button>
                </div>
              )}
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`category-${selectedCategory}`}>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="grid gap-3"
                    >
                      <AnimatePresence>
                        {filteredSections.map((section, index) => (
                          <Draggable
                            key={section.id}
                            draggableId={`section-${section.id}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <SectionCard
                                  section={section}
                                  onEdit={handleEdit}
                                  onDelete={handleDelete}
                                  dragHandleProps={provided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      </AnimatePresence>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <SectionModal
          section={editingSection}
          categories={sortedCategories}
          onClose={() => {
            setShowModal(false);
            setEditingSection(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AdminSections;